import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@nucleus/ui-base';
import {
    CapabilityControllerService,
    CapabilityFieldControllerService,
    CapabilityLifecycleControllerService,
    CapabilityRecord,
    CapabilityFieldRecord,
} from '../../api/capability-service';
import { DecommissionConfirmRequest } from '../../api/capability-service/model/decommissionConfirmRequest';
import { CapabilityFieldDialogComponent, CapabilityFieldDialogData } from './capability-field-dialog.component';
import { CapabilityDecommissionDialogComponent } from './capability-decommission-dialog.component';

/**
 * Capability detail / editor page. Phase 2b.
 *
 * <p>Read-only on ACTIVE / INACTIVE / DECOMMISSIONED; field-CRUD enabled
 * only on IN_PROGRESS (mirrors the backend's frozen-on-promote contract).
 *
 * <p>Toolbar actions:
 *   IN_PROGRESS → Promote, Delete draft, Add field
 *   ACTIVE      → Deactivate
 *   INACTIVE    → Decommission (opens dry-run dialog)
 *   DECOMMISSIONED → no actions
 */
@Component({
    selector: 'app-capability-detail',
    templateUrl: './capability-detail.component.html',
    styleUrl: './capability-detail.component.css',
    standalone: false,
})
export class CapabilityDetailComponent extends BaseComponent implements OnInit {

    private route: ActivatedRoute = inject(ActivatedRoute);
    protected override router: Router = inject(Router);
    protected override dialog: MatDialog = inject(MatDialog);
    private capabilityService: CapabilityControllerService = inject(CapabilityControllerService);
    private fieldService: CapabilityFieldControllerService = inject(CapabilityFieldControllerService);
    private lifecycleService: CapabilityLifecycleControllerService = inject(CapabilityLifecycleControllerService);

    capability = signal<CapabilityRecord | null>(null);
    loading    = signal<boolean>(true);

    inputFields = computed<CapabilityFieldRecord[]>(() =>
        (this.capability()?.fields ?? [])
            .filter(f => f.fieldRole === CapabilityFieldRecord.FieldRoleEnum.Input)
            .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0) || a.fieldName.localeCompare(b.fieldName)));

    outputFields = computed<CapabilityFieldRecord[]>(() =>
        (this.capability()?.fields ?? [])
            .filter(f => f.fieldRole === CapabilityFieldRecord.FieldRoleEnum.Output)
            .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0) || a.fieldName.localeCompare(b.fieldName)));

    fieldColumns = ['name', 'group', 'kind', 'type', 'cardinality', 'required', 'description', 'actions'];

    code    = '';
    version = '';

    isInProgress    = computed(() => this.capability()?.status === CapabilityRecord.StatusEnum.InProgress);
    isActive        = computed(() => this.capability()?.status === CapabilityRecord.StatusEnum.Active);
    isInactive      = computed(() => this.capability()?.status === CapabilityRecord.StatusEnum.Inactive);

    canManage(): boolean { return this.permissionChecker.hasAccessToManageCapabilities(); }

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToCapabilityManagement()) {
            this.showAccessDeny();
            return;
        }
        this.code    = this.route.snapshot.paramMap.get('code')    || '';
        this.version = this.route.snapshot.paramMap.get('version') || '';
        this.load();
    }

    refresh(): void { this.load(); }

    back(): void { this.router.navigate(['/administration/capabilities']); }

    statusClass(status: CapabilityRecord.StatusEnum | undefined): string {
        switch (status) {
            case CapabilityRecord.StatusEnum.Active:         return 'status-pill status-active';
            case CapabilityRecord.StatusEnum.InProgress:     return 'status-pill status-inprogress';
            case CapabilityRecord.StatusEnum.Inactive:       return 'status-pill status-inactive';
            case CapabilityRecord.StatusEnum.Decommissioned: return 'status-pill status-decommissioned';
            default: return 'status-pill';
        }
    }

    // ── lifecycle ───────────────────────────────────────────────────────────

    promote(): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress()) return;
        if (!confirm(`Promote ${this.code} ${this.version} to ACTIVE? This freezes the field shape.`)) return;
        this.loading.set(true);
        this.lifecycleService.promote(this.code, this.version).subscribe({
            next:  () => { this.showSuccess(`Promoted ${this.code} ${this.version}`); this.load(); },
            error: (e) => { this.showError(e, 'Promote failed'); this.loading.set(false); },
        });
    }

    deactivate(): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isActive()) return;
        if (!confirm(`Deactivate ${this.code} ${this.version}? Existing refs keep working; no new refs allowed.`)) return;
        this.loading.set(true);
        this.lifecycleService.deactivate(this.code, this.version).subscribe({
            next:  () => { this.showSuccess(`Deactivated ${this.code} ${this.version}`); this.load(); },
            error: (e) => { this.showError(e, 'Deactivate failed'); this.loading.set(false); },
        });
    }

    decommission(): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInactive()) return;
        const ref = this.dialog.open(CapabilityDecommissionDialogComponent, {
            data: { code: this.code, version: this.version },
            panelClass: 'bw-dialog-panel',
            width: '720px',
            disableClose: true,
        });
        ref.afterClosed().subscribe(actions => {
            if (!actions) return; // operator cancelled
            if (!this.permissionChecker.hasAccessToCapabilityManagement()) { this.showAccessDeny(); return; }
            this.loading.set(true);
            const body: DecommissionConfirmRequest = { schedules: actions };
            this.lifecycleService.decommissionConfirm(this.code, this.version, body).subscribe({
                next:  () => { this.showSuccess(`Decommissioned ${this.code} ${this.version}`); this.load(); },
                error: (e) => { this.showError(e, 'Decommission failed'); this.loading.set(false); },
            });
        });
    }

    deleteDraft(): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress()) return;
        if (!confirm(`Delete draft ${this.code} ${this.version}? Field rows cascade.`)) return;
        this.loading.set(true);
        this.capabilityService._delete(this.code, this.version).subscribe({
            next:  () => { this.showSuccess(`Deleted ${this.code} ${this.version}`); this.back(); },
            error: (e) => { this.showError(e, 'Delete failed'); this.loading.set(false); },
        });
    }

    // ── field CRUD ──────────────────────────────────────────────────────────

    addField(): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress()) return;
        this.openFieldDialog(null);
    }

    editField(field: CapabilityFieldRecord): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress()) return;
        this.openFieldDialog(field);
    }

    deleteField(field: CapabilityFieldRecord): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress()) return;
        if (!confirm(`Delete field ${field.fieldRole}/${field.fieldName}?`)) return;
        this.loading.set(true);
        this.fieldService.delete1(this.code, this.version, field.fieldRole, field.fieldName).subscribe({
            next:  () => { this.showSuccess(`Deleted field ${field.fieldName}`); this.load(); },
            error: (e: any) => { this.showError(e, 'Delete field failed'); this.loading.set(false); },
        });
    }

    private openFieldDialog(existing: CapabilityFieldRecord | null): void {
        const data: CapabilityFieldDialogData = {
            code: this.code,
            version: this.version,
            mode: existing ? 'edit' : 'create',
            field: existing,
        };
        const ref = this.dialog.open(CapabilityFieldDialogComponent, {
            data,
            panelClass: 'bw-dialog-panel',
            width: '720px',
            disableClose: true,
        });
        ref.afterClosed().subscribe(saved => {
            if (saved) this.load();
        });
    }

    // ── data load ───────────────────────────────────────────────────────────

    private load(): void {
        this.loading.set(true);
        this.capabilityService.detail(this.code, this.version).subscribe({
            next:  (rec) => { this.capability.set(rec); this.loading.set(false); },
            error: (e)   => { this.showError(e, 'Failed to load capability'); this.loading.set(false); },
        });
    }
}
